import React, { useCallback, useRef, useState } from "react";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import { toPng } from "html-to-image";
import ScreenshotDevicePicker, {
    SCREENSHOT_DEVICES,
    resolveScreenshotDevice,
} from "../../components/ScreenshotDevicePicker";
import ScreenshotLangPicker from "../../components/ScreenshotLangPicker";
import { appendScreenshotLang, useScreenshotTranslation } from "../../lib/screenshotI18n";
import {
    JIGSAW_MOBILE_TITLE,
    screenshotPath as jigsawScreenshotPath,
} from "../../prototype/mobile-jigsaw/routes";
import {
    getJigsawScreenshotScenes,
    isScreenshotSceneConfigured as isJigsawScreenshotSceneConfigured,
} from "../../prototype/mobile-jigsaw/screenshotScenes";
import { formatScreenshotAccessValidThroughLabel as formatJigsawScreenshotAccessValidThroughLabel } from "../../prototype/mobile-jigsaw/lib/screenshotAccess";
import { formatScreenshotAccessValidThroughLabel as formatWordWheelScreenshotAccessValidThroughLabel } from "../../prototype/mobile-word-wheel/lib/screenshotAccess";
import { formatScreenshotAccessValidThroughLabel as formatSlidingScreenshotAccessValidThroughLabel } from "../../prototype/mobile-sliding/lib/screenshotAccess";
import {
    SLIDING_PUZZLE_QUEST_TITLE,
    screenshotPath as slidingScreenshotPath,
} from "../../prototype/mobile-sliding/routes";
import {
    isScreenshotSceneConfigured,
    SLIDING_SCREENSHOT_SCENES,
} from "../../prototype/mobile-sliding/screenshotScenes";
import {
    WORD_WHEEL_QUEST_TITLE,
    screenshotPath as wordWheelScreenshotPath,
} from "../../prototype/mobile-word-wheel/routes";
import {
    isScreenshotSceneConfigured as isWordWheelScreenshotSceneConfigured,
    WORD_WHEEL_SCREENSHOT_SCENES,
} from "../../prototype/mobile-word-wheel/screenshotScenes";
import { COMPANY_LOGO_PATH } from "../advertisement/advertisementRoutes";
import { downloadPngDataUrl } from "./downloadPngDataUrl";
import { openCapturePagePopup } from "./openBrowserPopup";
import ScreenshotPreviewRow from "./ScreenshotPreviewRow";
import { WebAdShotArt } from "../../pages/advertisement/WebGoogleAdShots";
import { prepareWebAdCaptureNode } from "../../pages/advertisement/WebAdJigsawBoard";
import {
    WEB_AD_FRAME,
    webAdShotQueryPath,
    webAdShotsByGroup,
} from "../../pages/advertisement/webAdShots";

function openCapturePopup(path, windowName, device) {
    if (typeof window === "undefined") return;
    const href = `${window.location.origin}${path}`;
    openCapturePagePopup(href, windowName, device.width, device.height);
}

const TO_PNG_OPTS = {
    cacheBust: true,
    pixelRatio: 1,
    skipFonts: true,
};

const GALLERY_APPS = {
    sliding: {
        title: SLIDING_PUZZLE_QUEST_TITLE,
        scenes: () => SLIDING_SCREENSHOT_SCENES.filter(isScreenshotSceneConfigured),
        screenshotPath: slidingScreenshotPath,
        windowPrefix: "puzzle-sliding-screenshot",
        validThroughLabel: formatSlidingScreenshotAccessValidThroughLabel,
    },
    jigsaw: {
        title: JIGSAW_MOBILE_TITLE,
        scenes: (device) =>
            getJigsawScreenshotScenes(device).filter(isJigsawScreenshotSceneConfigured),
        screenshotPath: jigsawScreenshotPath,
        windowPrefix: "puzzle-jigsaw-screenshot",
        validThroughLabel: formatJigsawScreenshotAccessValidThroughLabel,
    },
    "word-wheel": {
        title: WORD_WHEEL_QUEST_TITLE,
        scenes: () => WORD_WHEEL_SCREENSHOT_SCENES.filter(isWordWheelScreenshotSceneConfigured),
        screenshotPath: wordWheelScreenshotPath,
        windowPrefix: "puzzle-word-wheel-screenshot",
        validThroughLabel: formatWordWheelScreenshotAccessValidThroughLabel,
    },
};

const WEB_TILE_W = 280;

function WebAdGalleryTile({ shot }) {
    const frame = WEB_AD_FRAME[shot.asset];
    const scale = WEB_TILE_W / frame.width;
    const frameRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [exportLite, setExportLite] = useState(false);
    const [error, setError] = useState(null);
    const fileName = `${shot.fileStem}-${frame.width}x${frame.height}.png`;
    const path = webAdShotQueryPath(shot);
    const isJigsaw = shot.id === "jigsaw";

    const handleDownload = useCallback(async () => {
        const node = frameRef.current;
        if (!node || saving) return;
        setSaving(true);
        setError(null);
        if (isJigsaw) {
            setExportLite(true);
            for (let i = 0; i < 40; i += 1) {
                await new Promise((resolve) =>
                    requestAnimationFrame(() => requestAnimationFrame(resolve)),
                );
                const images = node.querySelectorAll(
                    "[data-web-ad-jigsaw] image[href], [data-web-ad-jigsaw] image",
                );
                if (images.length > 0 && images.length <= 3) break;
                await new Promise((resolve) => setTimeout(resolve, 30));
            }
        }
        try {
            await prepareWebAdCaptureNode(node);
            const dataUrl = await toPng(node, {
                ...TO_PNG_OPTS,
                width: frame.width,
                height: frame.height,
                backgroundColor: "#F8FAFC",
                onImageErrorHandler: () => undefined,
            });
            await downloadPngDataUrl(dataUrl, fileName);
        } catch (e) {
            setError(e?.message || "Download failed");
        } finally {
            setExportLite(false);
            setSaving(false);
        }
    }, [fileName, frame.height, frame.width, isJigsaw, saving]);

    return (
        <Box sx={{ width: WEB_TILE_W, flex: "0 0 auto" }}>
            <Link
                component={RouterLink}
                to={path}
                underline="none"
                sx={{ display: "block", color: "inherit" }}
            >
                <Box
                    sx={{
                        width: WEB_TILE_W,
                        height: frame.height * scale,
                        borderRadius: 1,
                        overflow: "hidden",
                        boxShadow: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        mb: 1,
                    }}
                >
                    <Box
                        sx={{
                            width: frame.width,
                            height: frame.height,
                            transform: `scale(${scale})`,
                            transformOrigin: "top left",
                            pointerEvents: "none",
                        }}
                    >
                        <WebAdShotArt shot={shot} width={frame.width} height={frame.height} />
                    </Box>
                </Box>
            </Link>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                {shot.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                {frame.width}×{frame.height} · {frame.ratio}
            </Typography>
            <Button
                size="small"
                variant="outlined"
                startIcon={saving ? <CircularProgress size={14} /> : <SaveAltIcon />}
                onClick={handleDownload}
                disabled={saving}
                sx={{ textTransform: "none", mb: 0.5 }}
            >
                Download PNG
            </Button>
            {error ? (
                <Typography variant="caption" color="error" sx={{ display: "block" }}>
                    {error}
                </Typography>
            ) : null}
            <Box
                aria-hidden
                sx={{
                    position: "fixed",
                    left: -10000,
                    top: 0,
                    width: frame.width,
                    height: frame.height,
                    overflow: "hidden",
                    pointerEvents: "none",
                }}
            >
                <WebAdShotArt
                    shot={shot}
                    width={frame.width}
                    height={frame.height}
                    frameRef={frameRef}
                    exportLite={exportLite}
                />
            </Box>
        </Box>
    );
}

function WebGoogleAdsGallery() {
    const sections = webAdShotsByGroup();

    return (
        <Box sx={{ p: 2, minWidth: 0 }}>
            <Box sx={{ mb: 2, minWidth: 0 }}>
                <Typography variant="h6" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Web Google Ads — All Screenshots
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Nine English sample creatives plus the existing 512×512 logo. Grouped by app.
                    Download PNG at the Google Ads size, or open a shot for a larger preview.
                </Typography>
            </Box>
            {sections.map(({ group, shots }) => (
                <Box key={group} sx={{ mb: 3.5 }}>
                    <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, mb: 1.25, color: "text.primary" }}
                    >
                        {group}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2.5,
                            alignItems: "flex-start",
                        }}
                    >
                        {shots.map((shot) => (
                            <WebAdGalleryTile key={`${shot.asset}-${shot.id}`} shot={shot} />
                        ))}
                    </Box>
                </Box>
            ))}
            <Box sx={{ mb: 2 }}>
                <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, mb: 1.25, color: "text.primary" }}
                >
                    Logo
                </Typography>
                <Box sx={{ width: WEB_TILE_W, flex: "0 0 auto" }}>
                    <Box
                        sx={{
                            width: WEB_TILE_W,
                            height: WEB_TILE_W,
                            borderRadius: 1,
                            border: "1px dashed",
                            borderColor: "divider",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "grey.50",
                            mb: 1,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ px: 2 }}>
                            Company logo
                            <br />
                            512×512
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                        Logo (512×512)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                        Existing capture — not a 1200 asset
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        component={RouterLink}
                        to={COMPANY_LOGO_PATH}
                        sx={{ textTransform: "none" }}
                    >
                        Open logo page
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

/**
 * Admin popup page — all screenshot scenes for one app in a wrapping gallery.
 */
export default function ScreenshotGalleryAllPage() {
    const { app } = useParams();
    const [searchParams] = useSearchParams();
    const { lang } = useScreenshotTranslation();

    if (app === "web") {
        return <WebGoogleAdsGallery />;
    }

    const config = GALLERY_APPS[app];
    const deviceKey = resolveScreenshotDevice(searchParams.get("device"));
    const device = SCREENSHOT_DEVICES[deviceKey];

    if (!config) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">
                    Unknown app &quot;{app}&quot;. Use sliding, jigsaw, word-wheel, or web.
                </Typography>
            </Box>
        );
    }

    const scenes = config.scenes(deviceKey);
    const { title, screenshotPath, windowPrefix, validThroughLabel } = config;
    const storeNote =
        app === "jigsaw"
            ? deviceKey === "pc"
                ? " PC shots are landscape 1920×1080 for Google Play on PC."
                : device.group === "apple"
                  ? " Apple shots use the in-app play screen, not Google Play marketing layouts."
                  : " Google Play shots keep the original marketing layouts."
            : (app === "word-wheel" || app === "sliding") && deviceKey === "pc"
              ? " PC shots are landscape 1920×1080 for Google Play on PC."
              : "";

    const openScene = (path, slug) => (e) => {
        e.preventDefault();
        openCapturePopup(
            appendScreenshotLang(path, lang, deviceKey),
            `${windowPrefix}-${deviceKey}-${slug}`,
            device,
        );
    };

    return (
        <Box sx={{ p: 2, minWidth: 0 }}>
            <Box sx={{ mb: 2, minWidth: 0 }}>
                <Typography variant="h6" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {title} — All Screenshots
                </Typography>
                <ScreenshotLangPicker sx={{ mb: 1.25 }} />
                <ScreenshotDevicePicker device={deviceKey} sx={{ mb: 1.25 }} />
                <Typography variant="body2" color="text.secondary">
                    Click a preview to open a {device.label.toLowerCase()} capture window
                    ({device.width}×{device.height})
                    {device.fileHint ? ` · export as ${device.fileHint}` : ""}.
                    {storeNote}
                </Typography>
                <Typography variant="body2" sx={{ color: "error.main", fontWeight: 600, mt: 1 }}>
                    Available through {validThroughLabel()}.
                </Typography>
            </Box>
            <ScreenshotPreviewRow
                scenes={scenes}
                screenshotPath={screenshotPath}
                lang={lang}
                onOpenScene={openScene}
                tileWidth={deviceKey === "pc" ? 280 : 160}
                device={deviceKey}
            />
        </Box>
    );
}
